import axios from 'axios';
import {IMPERSONATION_SESSION_HEADER, clearImpersonation, getImpersonation} from 'utils/impersonation';

const apiUrl = import.meta.env.API_URL;

const removeTokens = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh');
};
const createAPI = () => {
  try {
    const token: any = localStorage.getItem('token');
    const tokenParts = JSON.parse(window.atob(token.split('.')[1]));
    const now = Math.ceil(Date.now() / 1000);

    if (tokenParts.exp < now) {
      removeTokens();
    }
  } catch (e) {
    removeTokens();
  }
  const token = localStorage.getItem('token');
  return {
    baseURL: `${apiUrl}/api`,
    timeout: 60000,
    headers: {
      Authorization: token ? `Bearer ${token}` : null,
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  };
};

const ApiService = axios.create(createAPI());

// Read per request rather than baked into the instance headers: impersonation starts and stops
// without the module being re-evaluated, and every request has to carry the current state.
ApiService.interceptors.request.use(config => {
  const impersonation = getImpersonation();
  if (impersonation) {
    config.headers[IMPERSONATION_SESSION_HEADER] = String(impersonation.sessionId);
  } else {
    delete config.headers[IMPERSONATION_SESSION_HEADER];
  }
  return config;
});

ApiService.interceptors.response.use(
  response => {
    return response;
  },
  // eslint-disable-next-line func-names
  async function (error) {
    const originalRequest = error.config;

    // The impersonation session is gone (ended elsewhere, expired, or revoked because the
    // target is no longer eligible). Drop the stale local state and reload as the real user,
    // rather than leaving every request 403ing against a session that no longer exists.
    if (error.response?.status === 403 && error.response?.data?.code === 'impersonation_invalid') {
      clearImpersonation();
      window.location.assign('/home');
      return Promise.reject(error);
    }

    if (typeof error.response === 'undefined') {
      console.log(
        'A server/network error occurred. ' +
          'Looks like CORS might be the problem. ' +
          'Sorry about this - we will get it fixed shortly.',
      );
      return Promise.reject(error);
    }

    if (error.response.status === 401 && originalRequest.url === '/token/refresh/') {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh');
      window.location.href = '/sign-in';
      return Promise.reject(error);
    }

    if (
      error.response.data.code === 'token_not_valid' &&
      error.response.status === 401 &&
      error.response.statusText === 'Unauthorized'
    ) {
      const refreshToken = localStorage.getItem('refresh');

      if (refreshToken) {
        const tokenParts = JSON.parse(window.atob(refreshToken.split('.')[1]));

        // exp date in token is expressed in seconds, while now() returns milliseconds:
        const now = Math.ceil(Date.now() / 1000);
        console.log(tokenParts.exp);

        if (tokenParts.exp > now) {
          return ApiService.post('/token/refresh/', {refresh: refreshToken})
            .then(response => {
              if (response.data.access) {
                localStorage.setItem('token', response.data.access);
              }
              if (response.data.refresh) {
                localStorage.setItem('refresh', response.data.refresh);
              }

              ApiService.defaults.headers.Authorization = `Bearer ${response.data.access}`;
              originalRequest.headers.Authorization = `Bearer ${response.data.access}`;

              return ApiService(originalRequest);
            })
            .catch(err => {
              console.log('Refresh token is not valid', err);
              window.location.href = '/sign-in';
            });
        }
        console.log('Refresh token is expired', tokenParts.exp, now);
        window.location.href = '/sign-in';
      } else {
        console.log('Refresh token not available.');
        window.location.href = '/sign-in';
      }
    }

    // specific error handling done elsewhere
    return Promise.reject(error);
  },
);

export default ApiService;
